<script setup lang="ts" generic="T extends Array<Record<string, any>>">
const props = defineProps<{ items: T }>();
const parent = useTemplateRef('parent');
const tabs = useTemplateRef('tabs');
const indicator = useTemplateRef('indicator');
const activeItem = defineModel<typeof props.items[number]>();
const activeIndex = ref(0);
const interval = useInterval(12000, {
    controls: true,
    callback: () => nextItem(),
});

type TItem = typeof props.items[number];
function activeTab(item: TItem | undefined, index: number) {
    if (!tabs.value || !item) return;

    activeIndex.value = index;
    activeItem.value = item;
    moveActiveIndicator(parent, indicator, tabs, index);
}

function handleOnClickTab(item: TItem, index: number) {
    interval.pause();
    activeTab(item, index);
    interval.reset();
    interval.resume();
};

function nextItem() {
    if (!props.items?.length || !tabs.value) return;

    const index = (activeIndex.value + 1) % props.items.length;
    activeTab(props.items[index], index);
};

onMounted(async () => {
    if (!props.items?.length || !tabs.value?.length) return;

    await nextTick();
    activeTab(props.items[0], 0);
});
</script>

<template>
    <div 
        ref="parent"
        class="w-fit flex items-center justify-center gap-5 relative p-2 bg-gray-100/15 rounded-full mx-auto
            overflow-hidden my-8 overflow-x-auto"
    >
        <svg ref="indicator" class="absolute h-[calc(100%-16px)] bg-white transition-transform duration-500 rounded-full right-0"></svg>
        <h3 
            v-for="item, index in props.items"
            :key="item.title"
            ref="tabs"
            class="w-fit py-2 px-4 relative z-1 flex justify-center items-center cursor-pointer text-body1-bold 
            transition-colors duration-300 select-none text-center"
            :class="activeItem?.title === item.title && 'text-primary-600'"
            @click="handleOnClickTab(item, index)"
        >
            {{ item.title }}
        </h3>
    </div>
</template>